import { Link, useNavigate } from 'react-router-dom';
import { LogOut, FolderOpen, ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/hooks/useAuth';
import { APP_NAME, ROUTES } from '@/lib/constants';
import { cn } from '@/lib/utils';

interface HeaderProps {
  /** Handler invoked when an unauthenticated user clicks "Sign in / Sign up". */
  onSignInClick?: () => void;
  className?: string;
}

function initials(name: string | null, email: string): string {
  const source = (name && name.trim()) || email;
  const parts = source.split(/[\s@.]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/**
 * Editorial masthead. Two states:
 *   - unauthenticated: shows a "Sign in / Sign up" ghost button
 *   - authenticated: shows a user dropdown with "My projects" + "Sign out"
 */
export function Header({ onSignInClick, className }: HeaderProps) {
  const { status, user, signOut } = useAuth();
  const navigate = useNavigate();

  const onSignOut = async () => {
    await signOut();
    navigate(ROUTES.home);
  };

  return (
    <header
      className={cn(
        'relative z-10 w-full border-b border-ink/15 bg-paper/80 backdrop-blur-sm',
        className,
      )}
    >
      <div className="mx-auto flex max-w-7xl items-baseline justify-between px-6 py-5">
        <Link to={ROUTES.home}>
          <span className="font-serif text-2xl italic leading-none tracking-tight text-ink">
            {APP_NAME}
          </span>
        </Link>

        <nav className="flex items-center gap-2">
          {status === 'authenticated' && user ? (
            <>
              <Button asChild variant="ghost" size="sm" className="font-mono text-xs">
                <Link to={ROUTES.projects}>
                  <FolderOpen className="h-4 w-4" />
                  Projects
                </Link>
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-2 pl-2 pr-3 font-mono text-xs"
                    aria-label="Account menu"
                  >
                    <Avatar className="h-7 w-7">
                      <AvatarFallback>{initials(user.displayName, user.email)}</AvatarFallback>
                    </Avatar>
                    <span className="hidden md:inline truncate max-w-[160px] normal-case">
                      {user.displayName ?? user.email}
                    </span>
                    <ChevronDown className="h-3 w-3 opacity-60" />
                  </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end" className="min-w-[220px]">
                  <DropdownMenuLabel>signed in as</DropdownMenuLabel>
                  <div className="px-2 pb-2">
                    <p className="truncate text-sm text-ink">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to={ROUTES.projects}>
                      <FolderOpen className="mr-2 h-4 w-4" />
                      My projects
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={onSignOut}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="font-mono text-xs"
              onClick={onSignInClick}
              disabled={status === 'loading'}
            >
              Sign in / Sign up
            </Button>
          )}
        </nav>
      </div>
    </header>
  );
}
